import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Pill from "../../../components/v3/Pill";
import Btn from "../../../components/v3/Btn";
import Field from "../../../components/v3/Field";
import BulkImportWizard from "../../../components/BulkImportWizard";
import { bdcApi } from "../../../services/bdcApi";
import { useRole } from "../../../hooks/useRole";

// Human copy for each mint-invitation outcome the API returns.
const INVITE_OUTCOME = {
  EMAIL_SENT: {
    tone: "info",
    text: (email) => `Invitation email sent to ${email}. They receive a single-use, email-bound link.`,
  },
  EXISTING_ACCOUNT_QUEUED: {
    tone: "info",
    text: (email) => `${email} already has an account — a consultant request has been queued for Platform Manager approval.`,
  },
  EXISTING_ACCOUNT_AWAITING_ACTIVATION: {
    tone: "warn",
    text: (email) => `${email} has an account that is not yet activated. Once they activate, the consultant request goes to the approval queue.`,
  },
};

const CLIENT_IMPORT_FIELDS = [
  { key: "email", label: "Email", required: true },
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "language", label: "Language" },
  { key: "consent_basis", label: "Consent basis (EXPRESS/EBR)" },
];

const CONSULTANT_INVITE_FIELDS = [
  { key: "email", label: "Email", required: true },
  { key: "first_name", label: "First name" },
  { key: "role_type", label: "Role (INVESTOR_RELATIONS/ANALYST/ADVISOR/OTHER)" },
];

const ENTITY_TONE = {
  INVESTOR: "blue",
  DEAL: "amber",
  CONSULTANT: "mint",
};

const CLAIM_STATUS_CLASS = {
  PENDING_CONFIRMATION: "s-rev",
  PENDING:              "s-rev",
  CONFIRMED:            "s-info",
  APPROVED:             "s-active",
  COMPLETED:            "s-active",
  REJECTED:             "s-fail",
  EXPIRED:              "s-mute",
  CANCELLED:            "s-mute",
};

function BDCReferrals() {
  const { hasRole } = useRole();
  // The canonical login payload sends role_name === 'CONSULTANT' for
  // consultants; legacy BDC sessions send 'BDC' and render as before. A
  // consultant cannot mint DEAL links, run the import wizards, or invite
  // sub-consultants — those surfaces are hidden for them (the API enforces it
  // too).
  const isConsultant = hasRole("CONSULTANT");

  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [claims, setClaims] = useState([]);
  const [error, setError] = useState(null);

  // Invite-a-consultant card (BDC-only).
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);

  const [linkForm, setLinkForm] = useState({ entity_type: "INVESTOR", custom_code: "" });
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkMsg, setLinkMsg] = useState(null);

  const [claimForm, setClaimForm] = useState({
    entity_type: "INVESTOR",
    target_email: "",
    target_name: "",
    supporting_notes: "",
  });
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimMsg, setClaimMsg] = useState(null);

  // Bulk import wizards (clients + sub-consultant invites). The consent
  // attestation is captured OUTSIDE the wizard so the transport-agnostic
  // component stays untouched; the API also enforces attestation===true.
  const [clientAttested, setClientAttested] = useState(false);
  const [clientWizardOpen, setClientWizardOpen] = useState(false);
  const [inviteAttested, setInviteAttested] = useState(false);
  const [inviteWizardOpen, setInviteWizardOpen] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [linksRes, claimsRes] = await Promise.all([
        bdcApi.listLinks({ limit: 25 }),
        bdcApi.listClaims({ limit: 25 }),
      ]);
      setLinks(linksRes?.data?.rows || []);
      setClaims(claimsRes?.data?.rows || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function createLink(e) {
    e.preventDefault();
    setLinkSubmitting(true);
    setLinkMsg(null);
    try {
      const body = { entity_type: linkForm.entity_type };
      if (linkForm.custom_code.trim()) body.custom_code = linkForm.custom_code.trim();
      const res = await bdcApi.createLink(body);
      setLinkMsg({ tone: "info", text: `Link created: ${res?.data?.link_token}` });
      setLinkForm({ entity_type: "INVESTOR", custom_code: "" });
      await refresh();
    } catch (err) {
      setLinkMsg({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Create failed",
      });
    } finally {
      setLinkSubmitting(false);
    }
  }

  async function submitClaim(e) {
    e.preventDefault();
    setClaimSubmitting(true);
    setClaimMsg(null);
    try {
      const body = {
        entity_type: claimForm.entity_type,
        target_email: claimForm.target_email.trim(),
      };
      if (claimForm.target_name.trim()) body.target_name = claimForm.target_name.trim();
      if (claimForm.supporting_notes.trim()) body.supporting_notes = claimForm.supporting_notes.trim();
      await bdcApi.createClaim(body);
      setClaimMsg({
        tone: "info",
        text: "Claim submitted — counterparty will receive a confirmation email.",
      });
      setClaimForm({ entity_type: "INVESTOR", target_email: "", target_name: "", supporting_notes: "" });
      await refresh();
    } catch (err) {
      setClaimMsg({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Submit failed",
      });
    } finally {
      setClaimSubmitting(false);
    }
  }

  async function copyLink(link) {
    // The share URL is built by the API (portal host + per-type landing path).
    // Synthesising it here from window.location shipped the ADMIN host to every
    // BDC (release-2026-08 D3). Refuse to guess when the API did not send one.
    const url = link && link.share_url;
    if (!url) {
      setLinkMsg({ tone: "danger", text: "Share link unavailable for this code — refresh the page or contact support." });
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setLinkMsg({ tone: "info", text: `Copied ${url}` });
    } catch {
      setLinkMsg({ tone: "danger", text: "Clipboard access denied — select + copy manually." });
    }
  }

  async function inviteConsultant(e) {
    e.preventDefault();
    setInviteSubmitting(true);
    setInviteMsg(null);
    try {
      const res = await bdcApi.bdcInviteConsultant({ email: inviteEmail.trim() });
      const outcome = res?.data?.invited;
      const spec = INVITE_OUTCOME[outcome];
      setInviteMsg(
        spec
          ? { tone: spec.tone, text: spec.text(inviteEmail.trim()) }
          : { tone: "info", text: `Invitation processed for ${inviteEmail.trim()}.` }
      );
      setInviteEmail("");
    } catch (err) {
      setInviteMsg({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Invite failed",
      });
    } finally {
      setInviteSubmitting(false);
    }
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Growth tools" />

      {error && <Alert tone="danger">{error}</Alert>}

      {process.env.REACT_APP_SHOW_TEST_BANNER === "true" && (
        <Alert tone="warn">
          <strong>Important:</strong> Do not use real client email addresses in
          this form. This environment is for testing purposes only. Please use
          internal team emails or your own personal email address when submitting
          test claims.
        </Alert>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "var(--gap-y)",
        }}
      >
        <section className="card">
          <div className="card__head">
            <div className="card__title">Persistent referral link</div>
          </div>
          <div className="card__body">
            <form onSubmit={createLink} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field
                as="select"
                label="Entity"
                name="link-entity-type"
                value={linkForm.entity_type}
                onChange={(e) => setLinkForm({ ...linkForm, entity_type: e.target.value })}
              >
                <option value="INVESTOR">Investor</option>
                {!isConsultant && <option value="DEAL">Deal</option>}
              </Field>
              <Field
                label="Custom code (optional)"
                name="link-custom-code"
                maxLength={64}
                value={linkForm.custom_code}
                onChange={(e) => setLinkForm({ ...linkForm, custom_code: e.target.value })}
                placeholder="e.g. YOURNAME-INVEST"
              />
              <Btn variant="primary" block type="submit" disabled={linkSubmitting}>
                {linkSubmitting ? "Creating..." : "Create link"}
              </Btn>
              {linkMsg && <Alert tone={linkMsg.tone}>{linkMsg.text}</Alert>}
            </form>

            {links.length > 0 && (
              <ul style={{ listStyle: "none", margin: "16px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {links.slice(0, 5).map((l) => (
                  <li
                    key={l.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto auto",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 0",
                      borderTop: "1px solid var(--bd)",
                    }}
                  >
                    <Pill tone={ENTITY_TONE[l.entity_type] || "gray"}>{l.entity_type}</Pill>
                    <code className="mono" style={{ fontSize: "var(--t-xs)", color: "var(--tx-2)", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {l.link_token}
                    </code>
                    {l.custom_code ? (
                      <Pill tone="amber">{l.custom_code}</Pill>
                    ) : <span />}
                    <Btn size="sm" type="button" onClick={() => copyLink(l)}>Copy</Btn>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card__head">
            <div className="card__title">Submit manual referral claim</div>
          </div>
          <div className="card__body">
            <form onSubmit={submitClaim} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field
                as="select"
                label="Entity type"
                name="claim-entity-type"
                value={claimForm.entity_type}
                onChange={(e) => setClaimForm({ ...claimForm, entity_type: e.target.value })}
              >
                <option value="INVESTOR">Investor</option>
                {!isConsultant && <option value="DEAL">Deal</option>}
              </Field>
              <Field
                label="Target email *"
                name="claim-target-email"
                type="email"
                required
                maxLength={255}
                value={claimForm.target_email}
                // trimmed as typed: the browser's email check runs before submit (D11)
                onChange={(e) => setClaimForm({ ...claimForm, target_email: e.target.value.trim() })}
              />
              <Field
                label="Name / company"
                name="claim-target-name"
                maxLength={255}
                value={claimForm.target_name}
                onChange={(e) => setClaimForm({ ...claimForm, target_name: e.target.value })}
              />
              <Field
                as="textarea"
                label="Supporting notes"
                name="claim-supporting-notes"
                rows={3}
                maxLength={2048}
                value={claimForm.supporting_notes}
                onChange={(e) => setClaimForm({ ...claimForm, supporting_notes: e.target.value })}
                placeholder="Where you met them, relationship context..."
              />
              <Btn variant="primary" block type="submit" disabled={claimSubmitting}>
                {claimSubmitting ? "Submitting..." : "Submit claim"}
              </Btn>
              {claimMsg && <Alert tone={claimMsg.tone}>{claimMsg.text}</Alert>}
            </form>
          </div>
        </section>

        {!isConsultant && (
          <section className="card">
            <div className="card__head">
              <div className="card__title">Invite a consultant</div>
            </div>
            <div className="card__body">
              <p style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)", margin: "0 0 12px" }}>
                Send one consultant an invitation by email. They receive a
                single-use, email-bound link. If they already have an account,
                a consultant request is queued for Platform Manager approval.
              </p>
              <form onSubmit={inviteConsultant} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Field
                  label="Consultant email"
                  name="invite-consultant-email"
                  type="email"
                  required
                  maxLength={255}
                  value={inviteEmail}
                  // trimmed as typed: the browser's email check runs before submit (D11)
                  onChange={(e) => setInviteEmail(e.target.value.trim())}
                  placeholder="consultant@example.com"
                />
                <Btn variant="primary" block type="submit" disabled={inviteSubmitting || !inviteEmail.trim()}>
                  {inviteSubmitting ? "Sending..." : "Send invitation"}
                </Btn>
                {inviteMsg && <Alert tone={inviteMsg.tone}>{inviteMsg.text}</Alert>}
              </form>
            </div>
          </section>
        )}

        {!isConsultant && (
        <>
        <section className="card">
          <div className="card__head">
            <div className="card__title">Import clients (CSV)</div>
          </div>
          <div className="card__body">
            <p style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)", margin: "0 0 12px" }}>
              Upload a CSV of your investor clients (max 500 rows). Each contact
              receives one invitation email with a newsletter opt-in, your
              personal signup link, and a referral confirmation — no further
              emails unless they opt in. A referral claim is filed per contact.
            </p>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: "var(--t-sm)", marginBottom: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={clientAttested}
                onChange={(e) => {
                  setClientAttested(e.target.checked);
                  if (!e.target.checked) setClientWizardOpen(false);
                }}
                style={{ marginTop: 3 }}
              />
              <span>
                I confirm each contact has given consent to be contacted or has
                an existing business relationship with me.
              </span>
            </label>
            <Btn
              variant="primary"
              type="button"
              disabled={!clientAttested}
              onClick={() => setClientWizardOpen(true)}
            >
              Upload client CSV
            </Btn>
            {clientWizardOpen && (
              <div style={{ marginTop: 16, border: "1px solid var(--bd)", borderRadius: 8, padding: 16 }}>
                <BulkImportWizard
                  targetFields={CLIENT_IMPORT_FIELDS}
                  maxRows={500}
                  onSubmit={(rows) =>
                    bdcApi.createImport({ type: "CLIENT_IMPORT", rows, attestation: true })
                  }
                  onPollJob={(jobId) => bdcApi.getImport(jobId)}
                  onClose={() => { setClientWizardOpen(false); refresh(); }}
                />
              </div>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card__head">
            <div className="card__title">Invite sub-consultants (CSV)</div>
          </div>
          <div className="card__body">
            <p style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)", margin: "0 0 12px" }}>
              Upload a CSV of consultants or staff to invite to your network
              (max 100 rows). Sends single-use, email-bound invitations;
              consultants require admin approval before their role is granted.
              Once approved, they earn the standard commissions and you earn the
              sponsor override on their clients' invested volume.
            </p>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: "var(--t-sm)", marginBottom: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={inviteAttested}
                onChange={(e) => {
                  setInviteAttested(e.target.checked);
                  if (!e.target.checked) setInviteWizardOpen(false);
                }}
                style={{ marginTop: 3 }}
              />
              <span>
                I confirm I have a professional relationship with these contacts
                and they expect to hear from me.
              </span>
            </label>
            <Btn
              variant="primary"
              type="button"
              disabled={!inviteAttested}
              onClick={() => setInviteWizardOpen(true)}
            >
              Upload consultant CSV
            </Btn>
            {inviteWizardOpen && (
              <div style={{ marginTop: 16, border: "1px solid var(--bd)", borderRadius: 8, padding: 16 }}>
                <BulkImportWizard
                  targetFields={CONSULTANT_INVITE_FIELDS}
                  maxRows={100}
                  onSubmit={(rows) =>
                    bdcApi.createImport({ type: "CONSULTANT_INVITES", rows, attestation: true })
                  }
                  onPollJob={(jobId) => bdcApi.getImport(jobId)}
                  onClose={() => { setInviteWizardOpen(false); refresh(); }}
                />
              </div>
            )}
          </div>
        </section>
        </>
        )}
      </div>

      <section className="card">
        <div className="card__head">
          <div className="card__title">My claims</div>
        </div>
        <div className="card__body card__body--flush">
          {claims.length === 0 ? (
            <Empty>No claims submitted yet.</Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => {
                  const statusKey = String(c.status || "").toUpperCase();
                  const statusClass = CLAIM_STATUS_CLASS[statusKey] || "s-mute";
                  return (
                    <tr key={c.id}>
                      <td>
                        <Pill tone={ENTITY_TONE[c.entity_type] || "gray"}>{c.entity_type}</Pill>
                      </td>
                      <td>
                        <div>{c.target_name || "—"}</div>
                        <div className="tbl-id">{c.target_email}</div>
                      </td>
                      <td>
                        <span className={`tbl-status ${statusClass}`}>{statusKey.replace(/_/g, " ")}</span>
                      </td>
                      <td className="muted">{new Date(c.createdAt).toLocaleDateString()}</td>
                      <td className="muted">
                        {c.counterparty_expires_at ? new Date(c.counterparty_expires_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </PageShell>
  );
}

export default BDCReferrals;
